import { useState, useCallback, useEffect } from 'react'
import {
  Stack,
  SearchBox,
  IColumn,
  IconButton,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  TextField,
  Toggle,
  Dialog,
  DialogType,
  DialogFooter,
  Dropdown,
  IDropdownOption,
  TooltipHost,
} from '@fluentui/react'
import { apiService } from '../services/apiService'
import { useApi, useMutation } from '../hooks/useApi'
import { useRoles } from '../hooks/useRoles'
import { DataTable } from '../components/DataTable'
import type { Owner, OwnerFormData, PagedResponse } from '../types'

const emptyFormData: OwnerFormData = {
  displayName: '',
  email: '',
  jobTitle: '',
  department: '',
  isActive: true,
}

const statusFilterOptions: IDropdownOption[] = [
  { key: 'active', text: 'Active only' },
  { key: 'all', text: 'All (include departed)' },
  { key: 'departed', text: 'Departed only' },
]

export function Owners() {
  const { isAdmin } = useRoles()
  const [searchInput, setSearchInput] = useState('') // immediate box value
  const [searchQuery, setSearchQuery] = useState('') // debounced value used for fetching
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('active')
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false)
  const [editingOwner, setEditingOwner] = useState<Owner | null>(null)
  const [formData, setFormData] = useState<OwnerFormData>(emptyFormData)
  const [deleteDialogOwner, setDeleteDialogOwner] = useState<Owner | null>(null)
  const pageSize = 25

  const fetchOwners = useCallback(
    () => apiService.getOwners({
      page,
      pageSize,
      search: searchQuery || undefined,
      includeDeparted: statusFilter !== 'active',
      accountStatus: statusFilter === 'departed' ? 'Disabled' : undefined,
    }),
    [page, pageSize, searchQuery, statusFilter]
  )

  const {
    data: owners,
    loading,
    error,
    refetch,
  } = useApi<PagedResponse<Owner>>(fetchOwners, [page, searchQuery, statusFilter])

  const createMutation = useMutation((data: OwnerFormData) => apiService.createOwner(data))
  const updateMutation = useMutation(({ id, data }: { id: number; data: OwnerFormData }) =>
    apiService.updateOwner(id, data)
  )
  const deleteMutation = useMutation((id: number) => apiService.deleteOwner(id))

  // Check if owner is synced from Azure (has entraUserId)
  const isAzureSynced = (owner: Owner) => !!owner.entraUserId
  const isDeparted = (owner: Owner) => owner.accountStatus === 'Disabled' || owner.accountStatus === 'Deleted'
  // The edit panel is read-only for Viewers, or for Azure-synced owners (edit those in Entra ID).
  const isOwnerReadOnly = (owner: Owner | null) => !isAdmin || (!!owner && isAzureSynced(owner))

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; color: string }> = {
      Active: { bg: '#dff6dd', color: '#107c10' },
      Disabled: { bg: '#fed9cc', color: '#a4262c' },
      Deleted: { bg: '#fde7e9', color: '#a4262c' },
      Unknown: { bg: '#f3f2f1', color: '#605e5c' },
    }
    const style = styles[status] || styles.Unknown
    return { backgroundColor: style.bg, color: style.color }
  }

  const columns: IColumn[] = [
    {
      key: 'displayName',
      name: 'Name',
      fieldName: 'displayName',
      minWidth: 180,
      onRender: (item: Owner) => (
        <span
          style={{
            fontWeight: 500,
            textDecoration: isDeparted(item) ? 'line-through' : 'none',
            color: isDeparted(item) ? '#a19f9d' : 'inherit',
          }}
          title={item.displayName}
        >
          {item.displayName}
        </span>
      ),
    },
    {
      key: 'email',
      name: 'Email',
      fieldName: 'email',
      minWidth: 220,
      onRender: (item: Owner) => (
        <span
          style={{
            color: isDeparted(item) ? '#a19f9d' : 'inherit',
          }}
          title={item.email}
        >
          {item.email}
        </span>
      ),
    },
    {
      key: 'jobTitle',
      name: 'Job Title',
      fieldName: 'jobTitle',
      minWidth: 150,
      onRender: (item: Owner) => {
        const value = item.jobTitle || '-'
        return (
          <span
            style={{ color: isDeparted(item) ? '#a19f9d' : 'inherit' }}
            title={value}
          >
            {value}
          </span>
        )
      },
    },
    {
      key: 'department',
      name: 'Department',
      fieldName: 'department',
      minWidth: 150,
      onRender: (item: Owner) => {
        const value = item.department || '-'
        return (
          <span
            style={{ color: isDeparted(item) ? '#a19f9d' : 'inherit' }}
            title={value}
          >
            {value}
          </span>
        )
      },
    },
    {
      key: 'applications',
      name: 'Applications',
      minWidth: 150,
      onRender: (item: Owner) => (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <span
            title="Primary"
            style={{ color: isDeparted(item) ? '#a19f9d' : 'inherit' }}
          >
            {item.primaryApplicationCount || 0} primary
          </span>
          <span
            title="Secondary"
            style={{ color: isDeparted(item) ? '#a19f9d' : '#605e5c' }}
          >
            {item.secondaryApplicationCount || 0} secondary
          </span>
        </Stack>
      ),
    },
    {
      key: 'source',
      name: 'Source',
      minWidth: 100,
      onRender: (item: Owner) => (
        <span
          title={isAzureSynced(item) ? 'Synced from Azure AD' : 'Manually created'}
          style={{
            padding: '4px 8px',
            borderRadius: 12,
            fontSize: 12,
            backgroundColor: isAzureSynced(item) ? '#deecf9' : '#f3f2f1',
            color: isAzureSynced(item) ? '#0078d4' : '#605e5c',
            opacity: isDeparted(item) ? 0.6 : 1,
          }}
        >
          {isAzureSynced(item) ? 'Azure Synced' : 'Manual'}
        </span>
      ),
    },
    {
      key: 'status',
      name: 'Status',
      minWidth: 100,
      onRender: (item: Owner) => {
        const status = item.accountStatus || (item.isActive ? 'Active' : 'Disabled')
        const badgeStyle = getStatusBadge(status)
        return (
          <TooltipHost
            content={
              item.departedAt
                ? `Departed: ${new Date(item.departedAt).toLocaleDateString()}`
                : item.lastVerifiedAt
                ? `Last verified: ${new Date(item.lastVerifiedAt).toLocaleDateString()}`
                : undefined
            }
          >
            <span
              style={{
                padding: '4px 8px',
                borderRadius: 12,
                fontSize: 12,
                ...badgeStyle,
              }}
            >
              {status}
            </span>
          </TooltipHost>
        )
      },
    },
    {
      key: 'actions',
      name: '',
      minWidth: 80,
      maxWidth: 80,
      isResizable: false,
      onRender: (item: Owner) => {
        const canEdit = isAdmin && !isAzureSynced(item)
        return (
          <Stack horizontal tokens={{ childrenGap: 4 }}>
            <IconButton
              iconProps={{ iconName: canEdit ? 'Edit' : 'View' }}
              title={canEdit ? 'Edit' : isAzureSynced(item) ? 'View (Azure synced - edit in Entra ID)' : 'View'}
              onClick={(e) => {
                e.stopPropagation()
                openEditPanel(item)
              }}
            />
            {canEdit && (
              <IconButton
                iconProps={{ iconName: 'Delete' }}
                title="Delete"
                onClick={(e) => {
                  e.stopPropagation()
                  setDeleteDialogOwner(item)
                }}
              />
            )}
          </Stack>
        )
      },
    },
  ]

  const openEditPanel = (owner?: Owner) => {
    if (owner) {
      setEditingOwner(owner)
      setFormData({
        displayName: owner.displayName,
        email: owner.email,
        jobTitle: owner.jobTitle || '',
        department: owner.department || '',
        isActive: owner.isActive,
      })
    } else {
      setEditingOwner(null)
      setFormData(emptyFormData)
    }
    setIsEditPanelOpen(true)
  }

  const handleSave = async () => {
    if (editingOwner) {
      const result = await updateMutation.mutate({ id: editingOwner.ownerId, data: formData })
      if (result) {
        setIsEditPanelOpen(false)
        refetch()
      }
    } else {
      const result = await createMutation.mutate(formData)
      if (result) {
        setIsEditPanelOpen(false)
        refetch()
      }
    }
  }

  const handleDelete = async () => {
    if (deleteDialogOwner) {
      await deleteMutation.mutate(deleteDialogOwner.ownerId)
      setDeleteDialogOwner(null)
      refetch()
    }
  }

  // Debounce the search box so we don't query on every keystroke (matters at 25K+ owners).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQuery(searchInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const handleSearch = (newValue?: string) => {
    setSearchInput(newValue || '')
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <div>
            <h1 className="page-title">Owners</h1>
            <p className="page-description">
              Manage application owners and their contact information
            </p>
          </div>
          {isAdmin && (
            <PrimaryButton
              iconProps={{ iconName: 'Add' }}
              text="Add Owner"
              onClick={() => openEditPanel()}
            />
          )}
        </Stack>
      </div>

      {/* Filters */}
      <div className="card">
        <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="end">
          <SearchBox
            placeholder="Search owners..."
            value={searchInput}
            onChange={(_, newValue) => handleSearch(newValue)}
            styles={{ root: { width: 300 } }}
          />
          <Dropdown
            label="Status"
            selectedKey={statusFilter}
            options={statusFilterOptions}
            onChange={(_, option) => {
              if (option) {
                setStatusFilter(option.key as string)
                setPage(1)
              }
            }}
            styles={{ root: { width: 180 } }}
          />
        </Stack>
      </div>

      {/* Results */}
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          styles={{ root: { marginBottom: 16 } }}
        >
          {error}
        </MessageBar>
      )}

      <div className="card">
        <DataTable
          items={owners?.items || []}
          columns={columns}
          loading={loading}
          emptyMessage="No owners found"
        />

        {/* Pagination */}
        {owners && owners.totalPages > 1 && (
          <Stack
            horizontal
            horizontalAlign="center"
            tokens={{ childrenGap: 8 }}
            styles={{ root: { marginTop: 16 } }}
          >
            <IconButton
              iconProps={{ iconName: 'ChevronLeft' }}
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            />
            <span style={{ padding: '8px 16px' }}>
              Page {page} of {owners.totalPages}
            </span>
            <IconButton
              iconProps={{ iconName: 'ChevronRight' }}
              disabled={page === owners.totalPages}
              onClick={() => setPage(p => p + 1)}
            />
          </Stack>
        )}
      </div>

      {/* Edit/Create Panel */}
      <Panel
        isOpen={isEditPanelOpen}
        onDismiss={() => setIsEditPanelOpen(false)}
        headerText={editingOwner ? (isOwnerReadOnly(editingOwner) ? 'View Owner' : 'Edit Owner') : 'Add Owner'}
        type={PanelType.medium}
        onRenderFooterContent={() => (
          isOwnerReadOnly(editingOwner) ? (
            <DefaultButton text="Close" onClick={() => setIsEditPanelOpen(false)} />
          ) : (
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <PrimaryButton
                text="Save"
                onClick={handleSave}
                disabled={createMutation.loading || updateMutation.loading}
              />
              <DefaultButton text="Cancel" onClick={() => setIsEditPanelOpen(false)} />
            </Stack>
          )
        )}
        isFooterAtBottom
      >
        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { marginTop: 20 } }}>
          {editingOwner && isOwnerReadOnly(editingOwner) ? (
            <>
              {isAzureSynced(editingOwner) ? (
                <MessageBar messageBarType={MessageBarType.info}>
                  This owner is synced from Azure AD. To modify this user, please update them in{' '}
                  <a href="https://entra.microsoft.com" target="_blank" rel="noopener noreferrer">
                    Microsoft Entra ID
                  </a>{' '}
                  and then resync via Settings.
                </MessageBar>
              ) : (
                <MessageBar messageBarType={MessageBarType.info}>
                  You have read-only (Viewer) access. Owner details are shown below; ask an
                  administrator to make changes.
                </MessageBar>
              )}
              <div>
                <span style={{ color: '#605e5c', fontSize: 12 }}>Display Name</span>
                <div style={{ fontWeight: 500 }}>{editingOwner.displayName}</div>
              </div>
              <div>
                <span style={{ color: '#605e5c', fontSize: 12 }}>Email</span>
                <div>{editingOwner.email}</div>
              </div>
              <div>
                <span style={{ color: '#605e5c', fontSize: 12 }}>Job Title</span>
                <div>{editingOwner.jobTitle || '-'}</div>
              </div>
              <div>
                <span style={{ color: '#605e5c', fontSize: 12 }}>Department</span>
                <div>{editingOwner.department || '-'}</div>
              </div>
              <div>
                <span style={{ color: '#605e5c', fontSize: 12 }}>Entra User ID</span>
                <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{editingOwner.entraUserId}</div>
              </div>
              <div>
                <span style={{ color: '#605e5c', fontSize: 12 }}>Account Status</span>
                <div
                  style={{
                    color: isDeparted(editingOwner) ? '#a4262c' : '#107c10',
                    fontWeight: 500,
                  }}
                >
                  {editingOwner.accountStatus || (editingOwner.isActive ? 'Active' : 'Inactive')}
                </div>
              </div>
              {editingOwner.lastVerifiedAt && (
                <div>
                  <span style={{ color: '#605e5c', fontSize: 12 }}>Last Verified</span>
                  <div>{new Date(editingOwner.lastVerifiedAt).toLocaleString()}</div>
                </div>
              )}
              {editingOwner.departedAt && (
                <div>
                  <span style={{ color: '#605e5c', fontSize: 12 }}>Departed At</span>
                  <div style={{ color: '#a4262c' }}>
                    {new Date(editingOwner.departedAt).toLocaleString()}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {(createMutation.error || updateMutation.error) && (
                <MessageBar messageBarType={MessageBarType.error}>
                  {createMutation.error || updateMutation.error}
                </MessageBar>
              )}
              <TextField
                label="Display Name"
                value={formData.displayName}
                onChange={(_, val) => setFormData({ ...formData, displayName: val || '' })}
                required
              />
              <TextField
                label="Email"
                value={formData.email}
                onChange={(_, val) => setFormData({ ...formData, email: val || '' })}
                type="email"
                required
              />
              <TextField
                label="Job Title"
                value={formData.jobTitle}
                onChange={(_, val) => setFormData({ ...formData, jobTitle: val })}
              />
              <TextField
                label="Department"
                value={formData.department}
                onChange={(_, val) => setFormData({ ...formData, department: val })}
              />
              <Toggle
                label="Active"
                checked={formData.isActive}
                onChange={(_, checked) => setFormData({ ...formData, isActive: !!checked })}
                onText="Active"
                offText="Inactive"
              />
            </>
          )}
        </Stack>
      </Panel>

      {/* Delete Confirmation Dialog */}
      <Dialog
        hidden={!deleteDialogOwner}
        onDismiss={() => setDeleteDialogOwner(null)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Delete Owner',
          subText: `Are you sure you want to delete "${deleteDialogOwner?.displayName}"? This action cannot be undone.`,
        }}
      >
        <DialogFooter>
          <PrimaryButton
            text="Delete"
            onClick={handleDelete}
            styles={{ root: { backgroundColor: '#a4262c', borderColor: '#a4262c' } }}
          />
          <DefaultButton text="Cancel" onClick={() => setDeleteDialogOwner(null)} />
        </DialogFooter>
      </Dialog>
    </div>
  )
}
